import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import Layout from '../components/Layout';
import MonthlySummaryModal from '../components/MonthlySummaryModal';
import TrialBanner from '../components/TrialBanner';
import MonthEndCountdownBanner from '../components/MonthEndCountdownBanner';
import QuickSetupCard from '../components/QuickSetupCard';
import WeeklyIntentionCard from '../components/WeeklyIntentionCard';
import { 
  CheckCircle2, ChevronDown, ChevronUp, ExternalLink,
  Target, Users, ArrowRight, BookOpen, CalendarCheck, UtensilsCrossed, UserCheck, Mail, FileDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { renderTextWithLinks } from '../lib/renderText';

// Helper function to render text with clickable links
const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [expandedActions, setExpandedActions] = useState({});
  const [loading, setLoading] = useState(true);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);

  // Fetch trial status
  const fetchTrialStatus = async () => {
    try {
      const res = await apiClient.get('/trial-status');
      setTrialStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch trial status:', err);
    }
  };

  // Action emoji icons matching the landing page style
  const actionEmojis = {
    'Hit Protein Target': '🥩',
    '2+ Cups Veggies': '🥗',
    'No Refined Sugar': '🚫',
    '2+ Litres Water': '💧',
    'No Alcohol': '🍷',
    'Balanced Dinner': '🍽️',
    'Meal Plan & Prep': '📋',
    'Strength Training': '💪',
    '30 Min Outside': '🚶',
    '7+ Hours Sleep': '😴',
  };

  // Fallback emojis by icon type
  const iconEmojis = {
    'beef': '🥩',
    'salad': '🥗',
    'candy-off': '🚫',
    'droplets': '💧',
    'wine': '🍷',
    'wine-off': '🍷',
    'utensils': '🍽️',
    'clipboard-list': '📋',
    'cooking-pot': '📋',
    'dumbbell': '💪',
    'footprints': '🚶',
    'moon': '😴',
    'brain': '🧠',
  };

  // Retry wrapper for API calls
  const fetchWithRetry = async (apiCall, retries = 2) => {
    for (let i = 0; i <= retries; i++) {
      try {
        return await apiCall();
      } catch (err) {
        if (i === retries) throw err;
        await new Promise(r => setTimeout(r, 1000));
      }
    }
  };

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const res = await fetchWithRetry(() => apiClient.get('/actions'));
        setActions(res.data);
      } catch (err) {
        console.error('Failed to fetch actions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchActions();
    fetchTrialStatus();
  }, []);

  // Check for new monthly summary on load
  useEffect(() => {
    const checkForNewSummary = async () => {
      try {
        const res = await apiClient.get('/summaries/check-new');
        if (res.data.has_new_summary) {
          // Fetch the full summary
          const summaryRes = await apiClient.get(`/summaries/${res.data.year}/${res.data.month}`);
          setCurrentSummary(summaryRes.data);
          setSummaryModalOpen(true);
        }
      } catch (err) {
        console.error('Failed to check for summary:', err);
      }
    };
    checkForNewSummary();
  }, []);

  const markSummaryViewed = async (year, month) => {
    try {
      await apiClient.post(`/summaries/${year}/${month}/mark-viewed`);
      // Close the modal after marking as viewed
      setSummaryModalOpen(false);
      setCurrentSummary(null);
    } catch (err) {
      console.error('Failed to mark summary as viewed:', err);
    }
  };

  const handleSummaryModalClose = () => {
    setSummaryModalOpen(false);
  };

  const toggleActionExpand = (actionId) => {
    setExpandedActions(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  // Display-name overrides — DB stores the original names as keys used across
  // check-in logic, points, etc. This just softens the label for members.
  const ACTION_NAME_OVERRIDES = {
    'No Alcohol': 'Limit Alcohol',
    'No Refined Sugar': 'Limit Refined Sugar',
  };
  const getActionDisplayName = (action) => ACTION_NAME_OVERRIDES[action.name] || action.name;

  const getActionEmoji = (action) => {
    // First try to match by name
    if (actionEmojis[action.name]) {
      return actionEmojis[action.name];
    }
    // Then try to match by icon
    if (iconEmojis[action.icon]) {
      return iconEmojis[action.icon];
    }
    // Default
    return '⭐';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F4F1]">
        {/* Month-end countdown + Quick Setup — padding collapses when both are empty */}
        <div className="px-4 sm:px-6 lg:px-8 [&:has(>div>*)]:pt-6 [&:has(>div>*)]:pb-6 sm:[&:has(>div>*)]:pb-8">
          <div className="max-w-5xl mx-auto space-y-3">
            <MonthEndCountdownBanner />
            <QuickSetupCard />
          </div>
        </div>

        {/* Weekly intention — floating popover (fixed-position, dismissible per week) */}
        <WeeklyIntentionCard />

        {/* Trial Banner - Shows for trial users */}
        {trialStatus?.is_trial && !trialStatus?.is_expired && (
          <div className="px-4 sm:px-6 lg:px-8 pt-6">
            <div className="max-w-5xl mx-auto">
              <TrialBanner 
                daysRemaining={trialStatus.days_remaining} 
                salesPageUrl={trialStatus.sales_page_url}
              />
            </div>
          </div>
        )}
        
        {/* Hero Section - 10 Best Practices (sales-page gym treatment) */}
        <section
          className="relative py-14 px-4 sm:px-6 lg:px-8 overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(30,22,14,0.62), rgba(30,22,14,0.58)), url("/best_practices_gym_bg.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          data-testid="dashboard-grit-practices-section"
        >
          <div className="max-w-5xl mx-auto relative">
            <div className="text-center mb-8">
              <button
                type="button"
                onClick={() => navigate('/my-actions')}
                className="inline-flex items-center gap-2 bg-[#EFB342] border border-[#B8871E] rounded-full px-5 py-2 mb-6 shadow-sm hover:bg-[#D99F30] active:scale-[0.98] transition"
                data-testid="dashboard-welcome-cta"
              >
                <CalendarCheck className="text-black" size={16} />
                <span className="text-sm text-black font-bold">
                  Welcome back {user?.name?.split(' ')[0] || 'Champion'}, take action &amp; collect your points now!
                </span>
              </button>

              <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-4">The 10 daily GRIT actions</p>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                Simple habits that <span className="text-[#EFB342]">support your menopause metabolism.</span>
              </h2>
              <p className="text-stone-200 text-sm sm:text-[15px] max-w-3xl mx-auto leading-relaxed">
                We gamify your metabolic health. Every single day, you&apos;ll log into your custom dashboard and track 10 crucial nutrition and lifestyle actions built specifically to protect your changing physiology.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
              {actions.map((action) => {
                const isExpanded = expandedActions[action.id];
                return (
                  <div 
                    key={action.id}
                    className={`bg-white border border-stone-200 rounded-xl p-3 sm:p-4 text-center hover:border-[#EFB342]/50 transition-all cursor-pointer ${isExpanded ? 'col-span-2 md:col-span-5' : ''}`}
                    onClick={() => toggleActionExpand(action.id)}
                    data-testid={`dashboard-action-${action.id}`}
                  >
                    {isExpanded ? (
                      <div className="text-left">
                        <div className="flex items-start gap-4">
                          <span className="text-3xl sm:text-4xl">{getActionEmoji(action)}</span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-stone-900 text-lg sm:text-xl">{getActionDisplayName(action)}</h4>
                              <ChevronUp size={20} className="text-stone-500" />
                            </div>
                            <p className="text-stone-600 text-xs sm:text-sm mt-2 leading-relaxed">
                              {renderTextWithLinks(action.description)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="text-2xl sm:text-3xl mb-2 block">{getActionEmoji(action)}</span>
                        <h4 className="font-semibold text-stone-900 text-sm sm:text-base mb-1">{getActionDisplayName(action)}</h4>
                        <div className="flex items-center justify-center gap-1 text-stone-500">
                          <span className="text-xs sm:text-sm">Tap to expand</span>
                          <ChevronDown size={12} />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <a
                href="https://buildnutrition.ca/wp-content/uploads/2026/10/RxGRIT-Daily-Action-Blueprint.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#EFB342] hover:text-white text-sm md:text-base font-semibold underline underline-offset-4 decoration-[#EFB342]/40 hover:decoration-white transition-colors"
                data-testid="dashboard-cheatsheet-link"
              >
                <FileDown size={18} />
                Download your 10 Daily Actions Cheat Sheet for quick reference.
              </a>
              <p className="text-stone-300 text-sm md:text-base leading-relaxed max-w-3xl mx-auto mt-8">
                These aren&apos;t random &ldquo;healthy habits&rdquo; — every RxGRIT action is a targeted biological lever that addresses the specific consequences of declining estrogen in the body. Protein protects muscle mass and resting metabolic rate. Limiting refined sugar targets insulin resistance. Sleep reduces cortisol and regulates hunger cues.
              </p>
              <p className="text-stone-300 text-sm md:text-base leading-relaxed max-w-3xl mx-auto mt-4">
                Earn <span className="text-white font-medium">1 point for every action taken every day</span>, climb the leaderboard, and build a metabolism that menopause can&apos;t derail.
              </p>
            </div>
          </div>
        </section>

        {/* Membership Features */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F5F4F1]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-3">
                BUT THAT'S NOT ALL!
              </h2>
              <p className="text-stone-600 text-base max-w-2xl mx-auto">
                Also included in your annual RxGRIT membership...
              </p>
            </div>
            
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${trialStatus?.is_trial ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
              {/* 0. Beyond Estrogen Guide — Featured Bonus Resource (paid members only) */}
              {!trialStatus?.is_trial && (
                <a
                  href="https://buildnutrition.ca/wp-content/uploads/2026/09/beyond-estrogen-guide-2.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative overflow-hidden rounded-2xl p-5 sm:p-6 group ring-2 ring-[#EFB342] hover:ring-[#B8871E] transition-all duration-300 shadow-md hover:shadow-xl bg-gradient-to-br from-[#FFF8E7] to-[#F9E9B8]"
                  data-testid="beyond-estrogen-card"
                >
                  <span className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase text-[#B8871E] mb-3">
                    Bonus Guide
                  </span>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-1 leading-tight">
                    Beyond Estrogen
                  </h3>
                  <p className="text-[#B8871E] italic text-xs mb-3 leading-snug">
                    A Guide to the Hidden Hormonal Players Dictating Your Weight, Energy &amp; Sleep.
                  </p>
                  <p className="text-stone-700 text-xs leading-relaxed">
                    + more on the 10 science-backed daily strategies to help you reclaim your metabolism.
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#B8871E] text-xs font-semibold mt-4 group-hover:underline">
                    Open the Guide <ExternalLink size={12} />
                  </span>
                </a>
              )}

              {/* 1. Access the Toolbox Card */}
              {trialStatus?.is_trial ? (
                <div className="bg-stone-100/50 border border-stone-300 rounded-2xl p-5 sm:p-6 opacity-70 cursor-not-allowed">
                  <div className="w-12 h-12 bg-[#EFB342]/20 rounded-xl flex items-center justify-center mb-4">
                    <BookOpen className="text-stone-500" size={24} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-600 mb-2">Access the Toolbox</h3>
                  <p className="text-stone-500 text-xs leading-relaxed">
                    Meal plans, recipes, macro guides, templates and more - DIY resources to crush the monthly challenges.
                  </p>
                  <p className="text-xs text-amber-500 mt-3">Available with full membership</p>
                </div>
              ) : (
                <a 
                  href="https://buildnutrition.ca/rxgrit-toolbox" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white border-2 border-stone-200 rounded-2xl p-5 sm:p-6 hover:border-[#EFB342]/50 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-[#EFB342]/10 rounded-xl flex items-center justify-center mb-4">
                    <BookOpen className="text-[#EFB342]" size={24} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-2 group-hover:text-[#EFB342] transition-colors">Access the Toolbox</h3>
                  <p className="text-stone-600 text-xs leading-relaxed">
                    Meal plans, recipes, macro guides, templates and more - DIY resources to crush the monthly challenges.
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#EFB342] text-xs mt-3 group-hover:underline">
                    Open Toolbox <ExternalLink size={12} />
                  </span>
                </a>
              )}

              {/* 2. Book 1:1 Support Card */}
              {trialStatus?.is_trial ? (
                <div className="bg-stone-100/50 border border-stone-300 rounded-2xl p-5 sm:p-6 opacity-70 cursor-not-allowed">
                  <div className="w-12 h-12 bg-[#EFB342]/20 rounded-xl flex items-center justify-center mb-4">
                    <Users className="text-stone-500" size={24} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-600 mb-2">Book 1:1 Support</h3>
                  <p className="text-stone-500 text-xs leading-relaxed">
                    DIY most days, 50% OFF coaching when needed.
                  </p>
                  <p className="text-xs text-amber-500 mt-3">Discount available with full membership</p>
                </div>
              ) : (
                <a 
                  href="https://p.bttr.to/3LHEaO9" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white border-2 border-stone-200 rounded-2xl p-5 sm:p-6 hover:border-[#EFB342]/50 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-[#EFB342]/10 rounded-xl flex items-center justify-center mb-4">
                    <Users className="text-[#EFB342]" size={24} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-2 group-hover:text-[#EFB342] transition-colors">Book 1:1 Support</h3>
                  <p className="text-stone-600 text-xs leading-relaxed">
                    DIY most days, 50% OFF coaching when needed.
                  </p>
                  <p className="text-xs mt-2 text-stone-600">
                    Use discount code <span className="font-bold text-red-500">RxGRIT50</span>
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#EFB342] text-xs mt-2 group-hover:underline">
                    Book Now <ExternalLink size={12} />
                  </span>
                </a>
              )}

              {/* 3. Recipe Database Card - Available to ALL users */}
              <a 
                href="https://app.thatcleanlife.com/shares/dd080ea7-162e-4480-8c0a-68a0fab08c56" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white border-2 border-stone-200 rounded-2xl p-5 sm:p-6 hover:border-[#EFB342]/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-[#EFB342]/10 rounded-xl flex items-center justify-center mb-4">
                  <UtensilsCrossed className="text-[#EFB342]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-bold text-stone-900 mb-2 group-hover:text-[#EFB342] transition-colors">Searchable Recipe Database</h3>
                <p className="text-stone-600 text-xs leading-relaxed">
                  <span className="font-bold text-[#EFB342]">*NEW*</span> Use password <span className="font-bold text-red-500">RxGRIT</span> to access, type your keyword into the 'Filter' field & voila, find what you need at your fingertips!
                </p>
                <span className="inline-flex items-center gap-1 text-[#EFB342] text-xs mt-3 group-hover:underline">
                  Search Recipes <ExternalLink size={12} />
                </span>
              </a>

              {/* 4. Free Macro Tracking App Card - Available to ALL users */}
              <a 
                href="https://buildnutrition.ca/pb-free-tracking-app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white border-2 border-stone-200 rounded-2xl p-5 sm:p-6 hover:border-[#EFB342]/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-[#EFB342]/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="text-[#EFB342]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-bold text-stone-900 mb-2 group-hover:text-[#EFB342] transition-colors">Free Macro Tracking App</h3>
                <p className="text-stone-600 text-xs leading-relaxed">
                  Optional — but a powerful tool if you want extra clarity and precision.
                </p>
                <span className="inline-flex items-center gap-1 text-[#EFB342] text-xs mt-3 group-hover:underline">
                  Login to Tracking App <ExternalLink size={12} />
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* About the Coach — matches sales page treatment */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F5F4F1]">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-10 shadow-sm">
              <div className="grid md:grid-cols-[auto,1fr] gap-8 md:gap-10 items-start">
                <div className="mx-auto md:mx-0 w-56 h-56 md:w-64 md:h-64 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/ho59sw0f_image.png"
                    alt="Yvette Styner, RHN"
                    className="w-full h-full object-cover object-[50%_20%]"
                    style={{ filter: 'saturate(0.85) brightness(1.05) contrast(0.95)' }}
                  />
                </div>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-2">Hi ya! I&apos;m Yvette Styner, RHN</h3>
                  <p className="text-stone-500 text-xs md:text-sm mb-6 leading-relaxed">
                    Registered Holistic Nutritionist&trade; · Menopause Metabolism Educator · Fellow Menopausal Woman
                  </p>
                  <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                    I created RxGRIT because knowing what to do isn&apos;t the same as doing it — and menopause makes that gap even wider. This is the daily support I wish every woman had beside her.
                  </p>
                  <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-6">
                    RxGRIT isn&apos;t a hands-off app. I&apos;m actively in here every day — answering questions, showing up in the community, and helping you stay accountable.
                  </p>
                  <p className="inline-block bg-[#EFB342]/20 text-stone-900 text-sm md:text-base font-medium leading-relaxed px-3 py-1.5 rounded-md">
                    <a href="mailto:yvette@buildnutrition.ca" className="underline hover:text-[#B8871E] transition-colors">Reach out</a> directly to me for help with anything, anytime. For real, I love hearing from you!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom padding for mobile */}
        <div className="h-20 sm:h-8"></div>
      </div>

      {/* Monthly Summary Modal */}
      <MonthlySummaryModal
        isOpen={summaryModalOpen}
        onClose={handleSummaryModalClose}
        summary={currentSummary}
        onMarkViewed={markSummaryViewed}
      />
    </Layout>
  );
};

export default UserDashboard;
